import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, InputNumber } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import ImageSelector from '../../components/ImageSelector';
import AllCategorySelector from '../../components/AllCategorySelector';
import UniImage from '../../components/UniImage';

const AllCategories = () => {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const urlTyp = urlParams.get('typ');

    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchTyp, setSearchTyp] = useState(null);
    const [searchParentUid, setSearchParentUid] = useState('');
    const [typList, setTypList] = useState([]);

    const fetchTypList = async () => {
        try {
            const response = await request.get('/go/sp/all/list_all_category_typ/');
            if (response.errno === 0) {
                setTypList(response.list || []);
            } else {
                Toast.error(response.errstr || '获取分类类型列表失败');
            }
        } catch (error) {
            console.error('获取分类类型列表失败:', error);
            Toast.error('获取分类类型列表失败');
        }
    };

    const statusMap = {
        0: { text: '显示', type: 'success' },
        1: { text: '隐藏', type: 'danger' },
    };

    useEffect(() => {
        if (urlTyp) {
            setSearchTyp(urlTyp);
        }
    }, [urlTyp]);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/all/list_all_category/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    typ: urlTyp || (searchTyp === null ? -1 : searchTyp),
                    parent_uid: searchParentUid
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
        fetchTypList();
    }, []);

    const handleAdd = async (values) => {
        const finalValues = {
            ...values,
            typ: urlTyp || values.typ
        };
        try {
            const response = await request.post('/go/sp/all/add_all_category/', {
                ...values,
                typ: parseInt(values.typ)
            });
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/all/edit_all_category/', {
                ...values,
                uid: currentRecord.uid,
                typ: parseInt(values.typ)
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/all/delete_all_category/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的分类');
            return;
        }
        try {
            const response = await request.post('/go/sp/all/batchedit_all_category/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '分类信息',
            dataIndex: 'name',
            width: 300,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {record.image && <UniImage src={record.image} width={40} height={40} style={{ borderRadius: 4 }} alt={text} />}
                    <div style={{ fontSize: '12px' }}>
                        <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                        {record.parent?.name && (
                            <div style={{ color: 'var(--semi-color-text-2)' }}>父级分类: {record.parent.name}</div>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: '分类类型',
            dataIndex: 'typ',
            width: 120,
            render: (text) => {
                const typItem = typList.find(item => item.uid == text);
                return typItem ? typItem.name : '-';
            }
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 100
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该分类吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const [addFormData, setAddFormData] = useState({
        name: '',
        image: '',
        parent_uid: '',
        sort: 0,
        status: 0,
        typ: ''
    });

    const [editFormData, setEditFormData] = useState({});

    useEffect(() => {
        if (currentRecord) {
            setEditFormData({
                ...currentRecord,
                parent_uid: currentRecord.parent?.uid || '',
                typ: currentRecord.typ
            });
        }
    }, [currentRecord]);

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const formData = isEdit ? editFormData : addFormData;
        const setFormData = isEdit ? setEditFormData : setAddFormData;

        const handleSubmit = () => {
            if (!formData.name) {
                Toast.error('请输入分类名称');
                return;
            }
            if (isEdit) {
                handleEdit(formData);
            } else {
                handleAdd(formData);
            }
        };

        return (
            <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>分类类型</div>
                    {urlTyp ? (
                        <Input value={typList.find(item => item.uid == urlTyp)?.name || ''} disabled style={{ width: '100%' }} />
                    ) : (
                        <Select
                            value={String(formData.typ)}
                            onChange={value => setFormData(prev => ({ ...prev, typ: value }))}
                            style={{ width: '100%' }}
                        >
                            {typList.map(item => (
                                <Select.Option key={item.uid} value={String(item.uid)}>{item.name}</Select.Option>
                            ))}
                        </Select>
                    )}
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>分类名称 <span style={{ color: 'var(--semi-color-danger)' }}>*</span></div>
                    <Input
                        value={formData.name}
                        onChange={value => setFormData(prev => ({ ...prev, name: value }))}
                        placeholder="请输入分类名称"
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>分类图标</div>
                    <ImageSelector
                        enableTDesignIcon={true}
                        value={formData.image}
                        onChange={value => setFormData(prev => ({ ...prev, image: value }))}
                        placeholder="请输入分类图标URL"
                        width="100%"
                        previewSize={100}
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>父级分类</div>
                    <AllCategorySelector
                        value={formData.parent_uid}
                        onChange={value => setFormData(prev => ({ ...prev, parent_uid: value }))}
                        placeholder="请选择父级分类"
                        excludeUid={currentRecord?.uid}
                        typ={formData.typ}
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>排序</div>
                    <InputNumber
                        value={formData.sort}
                        onChange={value => setFormData(prev => ({ ...prev, sort: value }))}
                        placeholder="请输入排序值（大的在前面）"
                        min={0}
                        precision={0}
                    />
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '8px' }}>状态</div>
                    <Select
                        value={formData.status}
                        onChange={value => setFormData(prev => ({ ...prev, status: value }))}
                        style={{ width: '100%' }}
                    >
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                </div>
                <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' onClick={handleSubmit}>确定</Button>
                    </Space>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.allCategoriesContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索分类名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="分类状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    {!urlTyp && (
                        <Select
                            placeholder="分类类型"
                            value={searchTyp}
                            onChange={setSearchTyp}
                            style={{ width: 150 }}
                            showClear
                        >
                            <Select.Option value={null}>全部类型</Select.Option>
                            {typList.map(item => (
                                <Select.Option key={item.uid} value={item.uid}>{item.name}</Select.Option>
                            ))}
                        </Select>
                    )}
                    <AllCategorySelector
                        placeholder="父级分类"
                        value={searchParentUid}
                        onChange={setSearchParentUid}
                        style={{ width: 200 }}
                        typ={searchTyp === null ? null : searchTyp}
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增分类</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量隐藏</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量显示</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个分类吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增分类"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑分类"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('edit')}
            </Modal>
        </div>
    );
};

export default AllCategories;